<script setup lang="ts">
import { bemBuilder } from "@chatfood/core-utils";
import { AtomText, AtomButton, AtomRadio } from "@chatfood/design-system";
import { ref, onMounted, computed } from "vue";
import { AddressBook } from "../address-book";
import { SchedulePicker } from "../schedule-picker";
import { BasketEventName } from "../basket-event-name";
import { BasketPax } from "../basket-pax";
import type { IDeliveryOrderingDetailsEmits } from "./emits";
import type { IDomainCustomerCoveredAddress } from "~/domain/customer-covered-address";
import { useBasketStore } from "~/stores/basket";
import { useCustomerStore } from "~/stores/customer";
import { useBusinessStore } from "~/stores/business";
import { useOutletStore } from "~/stores/outlet";
import { useDelivery } from "~/composables/use-delivery";
import { useNavigation } from "~/composables/use-navigation";
import { useLocale } from "~/composables/use-locale";
import { ETA_PREPARATION_MARGIN } from "~/domain/constants";
import type { IDomainOrderScheduledTime } from "~/domain/order-scheduled-time";
import type { IDomainOrderingModeSettings } from "~/domain/ordering-mode-settings";
import { useBugReporter, useDirectBackend } from "#imports";

const emits = defineEmits<IDeliveryOrderingDetailsEmits>();

const css = bemBuilder("delivery-ordering-details");

const { t } = useLocale();
const { onDeliverTo } = useDelivery();
const { push, resolvePath } = useNavigation();
const { outletRepo } = useDirectBackend();
const { report } = useBugReporter();

const business = useBusinessStore();
const outlet = useOutletStore();
const basket = useBasketStore();
const customer = useCustomerStore();

type IOrderType = "asap" | "scheduled";

const DELIVERY = "delivery";

const orderType = ref<IOrderType>(basket.scheduledTime ? "scheduled" : "asap");

const scheduledTime = ref<IDomainOrderScheduledTime | undefined>(
  basket.orderingMode === DELIVERY ? basket.scheduledTime : undefined,
);

const selectedAddressId = ref(basket.addressId);

const isSettingMode = ref(false);

const selectedOutletId = ref(basket.outletId);

const freshOrderingModeSettings = ref<IDomainOrderingModeSettings>();

const isActiveOrderingMode = computed(() => {
  return outlet.activeOrderingMode === DELIVERY;
});

const orderingModeSettings = computed(() => {
  return freshOrderingModeSettings.value ?? outlet.orderingModes.delivery;
});

const loadOrderingModeSettings = async (outletId: string) => {
  try {
    const [settings] = await outletRepo.getSettings({
      outletId,
      salesChannel: basket.salesChannel,
      orderingMode: [DELIVERY],
    });

    if (!settings) return;

    freshOrderingModeSettings.value = {
      ...settings,
      outletState: settings.operationalState,
    };
  } catch (e) {
    report(e);
  }
};

const selectedAddress = computed<IDomainCustomerCoveredAddress | undefined>(
  () => {
    const address = customer.addresses.find(
      (address) => address.id === selectedAddressId.value,
    );

    if (!address?.canDeliver) return;

    return address;
  },
);

const onSelectAddress = async (address: IDomainCustomerCoveredAddress) => {
  await loadOrderingModeSettings(address.deliveryCoverage.outletId);

  selectedOutletId.value = address.deliveryCoverage.outletId;
  selectedAddressId.value = address.id;

  scheduledTime.value = undefined;
  orderType.value = orderingModeSettings.value.acceptsAsapOrders
    ? "asap"
    : "scheduled";
};

const onConfirm = async () => {
  isSettingMode.value = true;

  try {
    if (
      selectedAddressId.value &&
      selectedAddressId.value !== basket.addressId
    ) {
      if (!selectedAddress.value) {
        isSettingMode.value = false;

        throw new Error(
          "[DeliveryOrderingDetails] Selected address id is not valid.",
        );
      }

      await onDeliverTo(selectedAddress.value, {
        lat: selectedAddress.value.lat,
        lng: selectedAddress.value.lng,
        addressId: selectedAddressId.value,
        address: selectedAddress.value?.location,
        number: selectedAddress.value?.number,
        city: selectedAddress.value?.city,
        country: business.country,
        postalCode: selectedAddress.value?.zipCode,
        instructions: selectedAddress.value?.instruction,
      });
    }

    if (basket.orderingMode !== DELIVERY) {
      await push("outlet", {
        orderingMode: DELIVERY,
        outletSlug: outlet.slug,
      });
    }

    if (business.isCatering) {
      await basket.setMeta({
        eventName: eventName.value ?? null,
        pax: typeof pax.value === "number" ? pax.value : null,
      });
    }

    await basket.setScheduledTime(scheduledTime.value);

    emits("update");
  } finally {
    isSettingMode.value = false;
  }
};

const onSetSchedule = (value?: IDomainOrderScheduledTime) => {
  scheduledTime.value = value;
};

const isButtonDisabled = computed<boolean>(() => {
  return (
    basket.currentLocation === undefined &&
    selectedAddressId.value === undefined
  );
});

onMounted(() => {
  if (isActiveOrderingMode.value) return;

  loadOrderingModeSettings(selectedOutletId.value);
});

const eventName = ref(basket.eventName);
const pax = ref(basket.pax);
</script>

<template>
  <div :class="css('')">
    <div :class="css('mode-config')">
      <AddressBook
        :active-address-id="selectedAddressId"
        :hide-deliver-cta="true"
        @selected="onSelectAddress"
      />
      <NuxtLink
        :to="
          resolvePath('order', {
            orderingMode: DELIVERY,
          })
        "
      >
        <AtomText type="link" color="color-primary">
          {{ t("components.delivery_ordering_details.change_outlet_cta") }}
        </AtomText>
      </NuxtLink>
    </div>
    <div v-if="business.isCatering" :class="css('meta-fields')">
      <AtomText type="subtitle">
        {{ t("components.delivery_ordering_details.details") }}
      </AtomText>
      <BasketEventName @update="(value) => (eventName = value)" />
      <BasketPax @update="(value) => (pax = value)" />
    </div>
    <div
      v-if="
        orderingModeSettings.acceptsAsapOrders ||
        orderingModeSettings.acceptsPreOrders
      "
      :class="css('scheduler')"
    >
      <AtomText type="subtitle">
        {{ t("components.delivery_ordering_details.schedule_title") }}
      </AtomText>
      <AtomRadio
        v-if="orderingModeSettings.acceptsAsapOrders"
        name="deliveryScheduleOrder"
        :checked="orderType === 'asap'"
        value="asap"
        @change="() => (orderType = 'asap')"
      >
        {{
          t("components.delivery_ordering_details.asap_radio", {
            from: orderingModeSettings.estimatedCompletionMinutes,
            to:
              orderingModeSettings.estimatedCompletionMinutes +
              ETA_PREPARATION_MARGIN,
          })
        }}
      </AtomRadio>
      <AtomRadio
        v-if="orderingModeSettings.acceptsPreOrders"
        name="deliveryScheduleOrder"
        :checked="orderType === 'scheduled'"
        value="scheduled"
        @change="() => (orderType = 'scheduled')"
      >
        {{ t("components.delivery_ordering_details.schedule_radio") }}
      </AtomRadio>
      <SchedulePicker
        v-if="
          orderingModeSettings.acceptsPreOrders &&
          orderType === 'scheduled' &&
          selectedOutletId
        "
        :is-root="true"
        :show-label="false"
        :ordering-mode="DELIVERY"
        :outlet-id="selectedOutletId"
        picker-align="row"
        :value="scheduledTime"
        @select="onSetSchedule"
        @vue:unmounted="() => onSetSchedule()"
      />
    </div>
    <div :class="css('footer')">
      <AtomButton
        size="large"
        :is-loading="isSettingMode"
        wide
        :is-disabled="isButtonDisabled"
        @click="onConfirm"
      >
        {{ t("components.delivery_ordering_details.main_cta") }}
      </AtomButton>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.delivery-ordering-details {
  min-width: 375px;

  &__mode-config {
    border-bottom: 1px solid var(--color-base-95);
    padding: var(--spacing-lg) var(--spacing-md);
    display: flex;
    flex-direction: column;
    gap: var(--spacing-lg);
    overflow: auto;
    max-height: 350px;

    @media (width < $breakpoint-lg) {
      max-height: 250px;
    }
  }

  &__meta-fields {
    display: flex;
    gap: var(--spacing-sm);
    flex-direction: column;
    border-bottom: 1px solid var(--color-base-95);
    padding: var(--spacing-md) var(--spacing-md) var(--spacing-lg);
  }

  &__outlet-card {
    cursor: default;
  }

  &__scheduler {
    border-bottom: 1px solid var(--color-base-95);
    padding: var(--spacing-lg) var(--spacing-md);
    display: flex;
    flex-direction: column;
    gap: var(--spacing-md);
  }

  &__footer {
    margin: var(--spacing-lg) var(--spacing-md);
  }
}
</style>
